import { useCallback } from 'react';
import { useToast } from 'hooks/useToast';
import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import {
  listSelectors,
  listActions,
} from 'store/reducers/menuCategoryReducer/listSlice';
import { detailActions } from 'store/reducers/menuCategoryReducer/detailSlice';
import { registrationActions } from 'store/reducers/menuCategoryReducer/registrationSlice';
import * as menuCategoryApi from 'api/menuCategoryApi';
import { MenuCategory } from 'types/menuCategory';
import { OriginalMenu } from 'types/menu';
import { menuRegistrationActions } from 'store/reducers/menuCategoryReducer/menuRegistrationSlice';
import { menuDetailActions } from 'store/reducers/menuCategoryReducer/menuDetailSlice';

/**
 * メニューカテゴリー一覧 store の実装をコンポーネントから隠すための中間層
 * @returns selector と action のラッパー
 */
export const useListStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const loading = useAppSelector(listSelectors.loading);
  const menuCategories = useAppSelector(listSelectors.filteredMenuCategories);
  const getMenus = useAppSelector(listSelectors.getMenus);

  const fetchMenusRelatedToCategories = useCallback(
    async (_menuCategories: MenuCategory[]) => {
      if (_menuCategories.length) {
        const results = await Promise.all(
          _menuCategories.map(async ({ id }) => {
            const result = await menuCategoryApi.fetchMenusRelatedToCategory(
              id,
            );

            return result;
          }),
        );

        const errorResult = results.find((r) => !r.ok);
        if (errorResult && !errorResult.ok) {
          toast({
            status: 'error',
            description: `メニュー一覧の取得に失敗しました: ${errorResult.err.message}`,
          });

          return;
        }

        results.forEach((result) => {
          if (result.ok) {
            dispatch(
              listActions.fetchMenusRelatedToCategorySuccess({
                menus: result.value,
              }),
            );
          }
        });
      }
    },
    [dispatch, toast],
  );

  const fetchMenuCategories = useCallback(async () => {
    dispatch(listActions.loadStarted());
    const result = await menuCategoryApi.fetchMenuCategories();
    if (result.ok) {
      dispatch(
        listActions.fetchMenuCategoriesSuccess({
          menuCategories: result.value,
        }),
      );

      await fetchMenusRelatedToCategories(result.value);
    } else {
      toast({
        status: 'error',
        description: `カテゴリー一覧の取得に失敗しました: ${result.err.message}`,
      });
    }
    dispatch(listActions.loadEnded());
  }, [dispatch, toast, fetchMenusRelatedToCategories]);

  const onOpenMenuCategoryRegistrationModal = useCallback(() => {
    dispatch(registrationActions.modalOpened());
  }, [dispatch]);

  const onOpenMenuRegistrationModal = useCallback(
    (categoryId: MenuCategory['id']) => {
      dispatch(menuRegistrationActions.modalOpened(categoryId));
    },
    [dispatch],
  );

  const onOpenMenuCategoryModal = useCallback(
    (menuCategory: MenuCategory) => {
      dispatch(detailActions.modalOpened(menuCategory));
    },
    [dispatch],
  );

  const onOpenMenuModal = useCallback(
    (menu: OriginalMenu) => {
      dispatch(menuDetailActions.modalOpened(menu));
    },
    [dispatch],
  );

  return {
    /** 一覧ロード状態 */
    loading,
    /** メニューカテゴリー一覧データ */
    menuCategories,
    /** メニューカテゴリー一覧を取得 */
    fetchMenuCategories,
    /** メニュー一覧を取得 */
    getMenus,
    /** メニューカテゴリー詳細を表示 */
    onOpenMenuCategoryModal,
    /** メニュー詳細を表示 */
    onOpenMenuModal,
    /** メニューカテゴリーの登録モーダル開く */
    onOpenMenuCategoryRegistrationModal,
    /** メニューの登録モーダル開く */
    onOpenMenuRegistrationModal,
  };
};
